import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { DURATIONS } from '../config/contracts';
import {
  formatUSDC,
  formatAPY,
  formatBlocksRemaining,
  getOpportunityStatus,
  generateOptionLabels,
  mapOpportunity
} from '../utils/helpers';
import StatusBadge from '../components/StatusBadge';
import './Markets.css';

function Markets() {
  const { provider, marketContract } = useWeb3();
  const navigate = useNavigate();

  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentBlock, setCurrentBlock] = useState(null);

  const loadOpportunities = useCallback(async () => {
    if (!marketContract) return;

    try {
      // Get current block
      if (provider) {
        const block = await provider.getBlockNumber();
        setCurrentBlock(block);
      }

      // Single call gets opportunities + stakes + bettors (newest first)
      const [rawOpps, stakes, bettors, ids] = await marketContract.getRecentOpportunitiesWithPools(50);

      if (rawOpps.length === 0) {
        setOpportunities([]);
        setFilteredOpportunities([]);
        setLoading(false);
        return;
      }

      // Map opportunities using the returned IDs
      const opps = rawOpps
        .map((opp, index) => {
          if (!opp.name) return null;
          const oppId = Number(ids[index]);
          return {
            ...mapOpportunity(opp, oppId),
            stakes: stakes[index],
            bettors: bettors[index]
          };
        })
        .filter(o => o !== null);

      setOpportunities(opps);
      setLoading(false);
    } catch (error) {
      console.error('Error loading opportunities:', error);
      setLoading(false);
    }
  }, [marketContract, provider]);

  useEffect(() => {
    loadOpportunities();
    const interval = setInterval(loadOpportunities, 10000);
    return () => clearInterval(interval);
  }, [loadOpportunities]);

  useEffect(() => {
    const filtered = opportunities.filter(opp => {
      if (filter === 'all') return true;
      return getOpportunityStatus(opp, currentBlock) === filter;
    });
    setFilteredOpportunities(filtered);
  }, [opportunities, filter, currentBlock]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  if (loading) {
    return <div className="loading">Loading opportunities...</div>;
  }

  return (
    <div className="markets-page">
      <div className="filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => handleFilterChange('all')}
        >
          All
        </button>
        <button
          className={`filter-btn ${filter === 'betting_open' ? 'active' : ''}`}
          onClick={() => handleFilterChange('betting_open')}
        >
          Betting Open
        </button>
        <button
          className={`filter-btn ${filter === 'monitoring' ? 'active' : ''}`}
          onClick={() => handleFilterChange('monitoring')}
        >
          Observation Window
        </button>
        <button
          className={`filter-btn ${filter === 'resolved' ? 'active' : ''}`}
          onClick={() => handleFilterChange('resolved')}
        >
          Resolved
        </button>
      </div>

      <div className="opportunities">
        {filteredOpportunities.length === 0 ? (
          <div className="empty">
            {opportunities.length === 0
              ? 'No opportunities available yet.'
              : 'No opportunities found for this filter.'
            }
          </div>
        ) : (
          filteredOpportunities.map(opp => (
            <OpportunityCard
              key={opp.id}
              opportunity={opp}
              currentBlock={currentBlock}
              onClick={() => navigate(`/opportunity/${opp.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function OpportunityCard({ opportunity: opp, currentBlock, onClick }) {
  const status = getOpportunityStatus(opp, currentBlock);
  const totalVolume = opp.stakes.reduce((sum, stake) => sum + stake, 0n);
  const totalBettors = opp.bettors ? opp.bettors.reduce((sum, count) => sum + Number(count), 0) : 0;
  const optionLabels = generateOptionLabels(opp.thresholds, opp.startApy);

  const observationStart = currentBlock ? formatBlocksRemaining(currentBlock, opp.startBlock) : 'Soon';
  const observationEnd = currentBlock ? formatBlocksRemaining(currentBlock, opp.endBlock) : 'Soon';

  return (
    <div className="opportunity-card" onClick={onClick}>
      <div className="opp-header">
        <div className="opp-title">
          <h3>{opp.name}</h3>
          <span className="duration-badge">
            {DURATIONS[Number(opp.durationEnum)]?.shortLabel || '?'}
          </span>
          <span className="bettors-badge">👤 {totalBettors}</span>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="opp-stats">
        <div className="stat">
          <div className="stat-label">Betting With Respect To</div>
          <div className="stat-value current-apy">{formatAPY(opp.startApy)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Total Volume</div>
          <div className="stat-value">${formatUSDC(totalVolume)}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Observation Starts</div>
          <div className="stat-value">{status === 'betting_open' ? observationStart : 'Started'}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Observation Ends</div>
          <div className="stat-value">{status === 'resolved' ? 'Resolved' : observationEnd}</div>
        </div>
      </div>

      <div className="options-grid">
        {opp.stakes.map((stake, index) => {
          const option = optionLabels[index];
          if (!option) return null;
          const bettorCount = opp.bettors ? Number(opp.bettors[index]) : 0;

          return (
            <div
              key={index}
              className={`option-card ${option.type}`}
              title={option.tooltip}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="option-main">{option.main}</div>
              <div className="option-sub">{option.sub}</div>
              <div className="option-footer">
                <span className="option-bettors">👤 {bettorCount}</span>
                <span className="option-stake">${formatUSDC(stake)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Markets;
